import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "react-datepicker/dist/react-datepicker.css";
import { ButtonWrapper, WrapperForm } from "../styles";
import { TextfieldCurrency, TextField } from "@habitech/keychain-atoms-inputs";
import AutocompleteMui from "../../common/Autocomplete/AutocompleteMui";
import { GridContainer, GridItem } from "../../common/grids";
import {
  DataAdvertiserType,
  DataStates,
  filtersDropdown,
  ZoneClasification,
} from "../constants";
import { useQualifyPropertyHabi } from "../hooks/QualifyPropertyHabi.hook";
import { Button, DropdownButton } from "@habitech/call-to-action";
import { useSelector } from "react-redux";
import { selectLoading } from "src/redux/qualify_for_habi/selects";

const validationSchema = Yup.object().shape({
  date_init: Yup.date().required("Fecha de inicio es requerida"),
  date_end: Yup.date()
    .required("Fecha de fin es requerida")
    .min(
      Yup.ref("date_init"),
      "La fecha de fin no puede ser anterior a la de inicio"
    ),
  price_min: Yup.string().required("Precio inicial es requerido"),
  price_max: Yup.number()
    .min(Yup.ref("price_min"), "El precio final debe ser mayor que el inicial")
    .required("Precio final es requerido"),
  leads_count: Yup.number()
    .min(1, "Cantidad de leads debe ser al menos 1")
    .required("Cantidad de leads es requerida")
    .max(1000, "Cantidad de leads debe ser menor de 1000"),
  city_id: Yup.array().test({
    name: "city_id",
    message: "Debe seleccionar al menos una ciudad.",
    test: (value, params) => {
      if (
        !params.parent["state_list"] ||
        params.parent["state_list"].length === 0
      ) {
        return true;
      }
      if (
        !!params.parent["state_list"] &&
        params.parent["state_list"].length > 0 &&
        value.length > 0
      ) {
        return true;
      }
      return false;
    },
  }),
});

const FormComponent = () => {
  const loadingGet = useSelector(selectLoading);
  const {
    municipalities,
    loading,
    handleSubmit,
    handleChangeState,
    handleChangeValues,
    handleChangeValuesAutocomplete,
    municipalitySelected,
    initialValues,
    feed_name_list,
    loading_feed
  } = useQualifyPropertyHabi();

  const today = new Date();
  const week = 1000 * 60 * 60 * 24 * 7;
  const lastWeek = new Date(today.getTime() - week);

  return (
    <WrapperForm>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isValid, errors }) => {
          return (
            <Form>
              <GridContainer>
                <GridItem
                  lg={3}
                  className="date-fields"
                  data-testid={"date-init-test"}
                >
                  <label>Fecha inicial de publicación:</label>
                  <Field name="date_init">
                    {({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          {...field}
                          data-testid={"date-picker-test"}
                          format={"dd/MM/yyy"}
                          defaultValue={lastWeek}
                          onChange={(date) =>
                            handleChangeValues(setFieldValue, "date_init", date)
                          }
                        />
                      </LocalizationProvider>
                    )}
                  </Field>
                  <ErrorMessage
                    name="date_init"
                    component="div"
                    className="error"
                  />
                </GridItem>
                <GridItem
                  lg={3}
                  className="date-fields"
                  data-testid={"date-end-test"}
                >
                  <label>Fecha final de publicación:</label>
                  <Field name="date_end">
                    {({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          {...field}
                          format={"dd/MM/yyy"}
                          defaultValue={today}
                          onChange={(date) =>
                            handleChangeValues(setFieldValue, "date_end", date)
                          }
                        />
                      </LocalizationProvider>
                    )}
                  </Field>
                  <ErrorMessage
                    name="date_end"
                    component="div"
                    className="error"
                  />
                </GridItem>

                <GridItem lg={3}>
                  <label>Precio Mínimo:</label>
                  <Field name="price_min">
                    {({ field }) => (
                      <div className="field-currency">
                        <TextfieldCurrency
                          {...field}
                          defaultValue={initialValues.price_min}
                          country="mx"
                          error={errors.price_min}
                          dataId={"price_min_test"}
                          onChange={(e, value) =>
                            handleChangeValues(
                              setFieldValue,
                              "price_min",
                              value
                            )
                          }
                        />
                      </div>
                    )}
                  </Field>
                  <ErrorMessage
                    name="price_min"
                    component="div"
                    className="error"
                  />
                </GridItem>

                <GridItem lg={3}>
                  <label>Precio Máximo:</label>
                  <Field name="price_max">
                    {({ field }) => (
                      <div className="field-currency">
                        <TextfieldCurrency
                          {...field}
                          country="mx"
                          error={errors.price_max}
                          defaultValue={initialValues.price_max}
                          dataId={"price_max_test"}
                          onChange={(e, value) =>
                            handleChangeValues(
                              setFieldValue,
                              "price_max",
                              value
                            )
                          }
                        />
                      </div>
                    )}
                  </Field>
                  <ErrorMessage
                    name="price_max"
                    component="div"
                    className="error"
                  />
                </GridItem>

                <GridItem lg={3} className="autocomplete">
                  <label>Tipo de anunciante:</label>
                  <Field name="broker_type">
                    {({ field }) => (
                      <AutocompleteMui
                        {...field}
                        data-id={"broker_type-autocomplete-test"}
                        options={DataAdvertiserType}
                        placeholder={
                          values.broker_type.length === 0
                            ? "Selecciona tipo de anunciante"
                            : ""
                        }
                        optionKey={"type"}
                        value={values.broker_type}
                        onChange={handleChangeValuesAutocomplete.bind(null, setFieldValue, "broker_type")}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="broker_type"
                    component="div"
                    className="error"
                  />
                </GridItem>

                <GridItem
                  lg={3}
                  className="autocomplete"
                  data-testid="state-list-test"
                >
                  <label>Estado:</label>
                  <AutocompleteMui
                    data-id="state-list-test"
                    options={DataStates}
                    optionKey={"label"}
                    onChange={handleChangeState.bind(null, setFieldValue)}
                    variant="white"
                    loading={false}
                    placeholder={
                      !!municipalitySelected && municipalitySelected > 0
                        ? ""
                        : "Seleccione estados"
                    }
                  />
                </GridItem>

                <GridItem lg={3} className="autocomplete">
                  <label>Ciudad:</label>
                  <Field name="city_id">
                    {({ field }) => (
                      <AutocompleteMui
                        {...field}
                        className={errors.city_id && "error"}
                        data-id={"city_id-autocomplete-test"}
                        loading={loading}
                        options={municipalities}
                        placeholder={
                          values.city_id.length === 0
                            ? "Selecciona tipo de ciudad"
                            : ""
                        }
                        optionKey={"name"}
                        value={values.city_id}
                        onChange={handleChangeValuesAutocomplete.bind(null, setFieldValue, "city_id")}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="city_id"
                    component="div"
                    className="error"
                  />
                </GridItem>

                <GridItem lg={3}>
                  <label>Número de Leads:</label>
                  <Field name="leads_count">
                    {({ field }) => (
                      <div className="field-leads">
                        <TextField
                          {...field}
                          error={errors.leads_count}
                          placeHolder={"Ingresa número de leads"}
                          onChange={(e, value) =>
                            handleChangeValues(
                              setFieldValue,
                              "leads_count",
                              value
                            )
                          }
                          dataId={"leads_count-test"}
                        />
                      </div>
                    )}
                  </Field>
                  <ErrorMessage
                    name="leads_count"
                    component="div"
                    className="error"
                  />
                </GridItem>

                <GridItem lg={3} className="autocomplete">
                  <label>Clasificación de zona:</label>
                  <Field name="zone_classification">
                    {({ field }) => (
                      <AutocompleteMui
                        {...field}
                        data-id={"test_id_zone_clasification"}
                        options={ZoneClasification}
                        placeholder={
                          values.zone_classification.length === 0
                            ? "Selecciona clasificación de zona"
                            : ""
                        }
                        optionKey={"label"}
                        value={values.zone_classification}
                        onChange={handleChangeValuesAutocomplete.bind(null, setFieldValue, "zone_classification")}
                      />
                    )}
                  </Field>
                </GridItem>

                {filtersDropdown.map((filter) => (
                  <GridItem key={`filter-${filter.test_id}`} lg={3} className="selector" data-testid={filter.test_id}>
                    <label>{filter.label}</label>
                    <Field name={filter.name}>
                      {({ field }) => (
                        <div className={filter.class_name}>
                          <DropdownButton
                            {...field}
                            dataId={filter.test_id}
                            listOptions={filter.list_options}
                            defaultSelected={filter.option_default}
                            originType="pcom"
                            setValue={(value) =>
                              handleChangeValues(
                                setFieldValue,
                                filter.name,
                                value
                              )
                            }
                            variant="white"
                          />
                        </div>
                      )}
                    </Field>
                  </GridItem>
                ))}

                {values.from_xml && String(values.from_xml) === "1" && <GridItem lg={3} className="autocomplete">
                  <label>Feed name:</label>
                  <Field name="feed_name">
                    {({ field }) => (
                      <AutocompleteMui
                        {...field}
                        data-id={"feed_name-autocomplete-test"}
                        loading={loading_feed}
                        options={feed_name_list}
                        placeholder={
                          values.feed_name?.length === 0
                            ? "Selecciona un feed name"
                            : ""
                        }
                        value={values.feed_name}
                        onChange={handleChangeValuesAutocomplete.bind(null, setFieldValue, "feed_name")}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="feed_name"
                    component="div"
                    className="error"
                  />
                </GridItem>}

                <GridItem lg={12} className="action-button">
                  <ButtonWrapper>
                    <Button
                      dataId=""
                      type="submit"
                      originType="pcom"
                      size="extra-large"
                      loading={loadingGet}
                      disabled={!isValid}
                    >
                      Aplicar filtro
                    </Button>
                  </ButtonWrapper>
                </GridItem>
              </GridContainer>
            </Form>
          );
        }}
      </Formik>
    </WrapperForm>
  );
};

export default FormComponent;
